import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import VideoPlayButton from "./VideoPlayButton";

type Props = {
  /**
   * Video source. A direct file URL (`.mp4`, `.webm`, …) renders a native
   * `<video>`; anything else (e.g. an embed URL) renders an `<iframe>`.
   */
  src: string;
  /** Optional poster image. For file sources, overrides the auto first-frame
   *  preview; for embed sources it's the still shown before play. */
  poster?: string;
  /**
   * Fill the parent (100% × 100%, no rounded frame) instead of the default
   * inline 16:9 card. Use for full-screen overlays.
   */
  fill?: boolean;
  /**
   * Start playing immediately on mount (skip the play-button preview). Only
   * use this when a recent user gesture allows unmuted autoplay — e.g. the
   * consultation outro, reached by clicking "Complete". For native `<video>`
   * sources, if the browser still blocks autoplay it falls back to the
   * branded play button.
   */
  autoPlay?: boolean;
  /**
   * Fired when playback finishes. Only emitted for native `<video>` sources —
   * embed `<iframe>`s can't report completion without the provider's JS API.
   */
  onEnded?: () => void;
  /** Fired when the media starts (the user clicks the play button). */
  onStarted?: () => void;
};

// Direct media file → native <video>; otherwise treat `src` as an embed URL.
const isFileSource = (src: string): boolean =>
  /\.(mp4|webm|ogg|mov|m4v|m3u8)(\?|#|$)/i.test(src);

// Append an autoplay flag so the embed starts as soon as it mounts (it only
// mounts after the user clicks the play button — a gesture — so autoplay with
// sound is allowed).
const withAutoplay = (url: string): string =>
  `${url}${url.includes("?") ? "&" : "?"}autoplay=1`;

// Media fragment that nudges the <video> to render an early frame as its
// preview (instead of a black box) when no explicit poster is provided.
const withPreviewFrame = (url: string): string =>
  `${url}${url.includes("#") ? "" : "#t=0.5"}`;

/**
 * Self-hosted-friendly video player, independent of react-player / the YouTube
 * JS API. Shows the branded play button over a preview; on click it plays.
 * Source-agnostic: a direct file plays in a native `<video>` (whose first
 * frame doubles as the preview), an embed URL plays in an `<iframe>`.
 */
const VideoPlayer = ({
  src,
  poster,
  fill = false,
  autoPlay = false,
  onEnded,
  onStarted,
}: Props) => {
  const isFile = isFileSource(src);
  // For file sources we can attempt autoplay on mount (and gracefully fall back
  // to the play button if the browser blocks it). For embeds we can't detect a
  // block, so they always start with the play button.
  const [playing, setPlaying] = useState(autoPlay && isFile);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (autoPlay && isFile && videoRef.current) {
      videoRef.current.play().then(
        () => onStarted?.(),
        () => setPlaying(false) // blocked → show the branded play button
      );
    }
    // Mount-only: autoplay attempt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPlayback = () => {
    setPlaying(true);
    onStarted?.();
    // Native <video> needs an explicit play() since it's already mounted.
    if (isFile) videoRef.current?.play().catch(() => {});
  };

  return (
    <Box sx={styles.frame(fill)}>
      <Box sx={styles.aspect(fill)}>
        {isFile ? (
          // Native video is mounted up front so its first frame shows as the
          // preview; controls + the branded overlay swap in on play.
          <>
            <Box
              component="video"
              ref={videoRef}
              src={poster ? src : withPreviewFrame(src)}
              poster={poster}
              preload="metadata"
              playsInline
              controls={playing}
              onPlay={() => setPlaying(true)}
              onEnded={onEnded}
              sx={styles.media}
            />
            {!playing && (
              <Box
                sx={styles.overlay}
                onClick={startPlayback}
                role="button"
                tabIndex={0}
                aria-label="Play video"
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    startPlayback();
                  }
                }}
              >
                <VideoPlayButton />
              </Box>
            )}
          </>
        ) : !playing ? (
          <Box
            sx={styles.poster(poster)}
            onClick={startPlayback}
            role="button"
            tabIndex={0}
            aria-label="Play video"
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                startPlayback();
              }
            }}
          >
            <VideoPlayButton />
          </Box>
        ) : (
          <Box
            component="iframe"
            src={withAutoplay(src)}
            sx={styles.media}
            title="Video player"
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
          />
        )}
      </Box>
    </Box>
  );
};

export default VideoPlayer;

const styles = {
  frame: (fill: boolean) =>
    fill
      ? {
          width: "100%",
          height: "100%",
          overflow: "hidden",
          // ✅ APPROVED: video letterbox backdrop, no theme token (#000000)
          backgroundColor: "#000000",
        }
      : {
          width: "100%",
          borderRadius: "8px",
          overflow: "hidden",
          backgroundColor: "#000000",
          boxShadow: "var(--box-shadow-general)",
        },
  aspect: (fill: boolean) =>
    fill
      ? { position: "relative" as const, width: "100%", height: "100%" }
      : { position: "relative" as const, width: "100%", paddingTop: "56.25%" }, // 16:9
  media: {
    position: "absolute" as const,
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    border: "none",
    display: "block",
  },
  // Click-to-play overlay over the preview frame. A subtle scrim keeps the
  // white play button readable over bright frames.
  overlay: {
    position: "absolute" as const,
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    backgroundColor: "rgba(0, 0, 0, 0.18)",
  },
  poster: (poster?: string) => ({
    position: "absolute" as const,
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    backgroundColor: "#000000",
    ...(poster
      ? {
          backgroundImage: `url(${poster})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }
      : {}),
  }),
};
